// Volcanoes (render-only prototype): the identity seam.
//
// PLACEHOLDER: today the sim publishes no volcano data. This module derives
// "which tiles are volcanoes, and dormant vs active" from the props the
// client already receives (`biome`, `level`, `q`, `r`) with a deterministic
// rule. A future sim-provided tile prop (`volcano: 'dormant' | 'active'`,
// see docs/volcano-design.md) wins over the derivation when present, so
// swapping the placeholder for real data touches only this file.
// `erupting` is ONLY ever reached through that explicit prop (never derived),
// so it is inert on real boards today.
//
// Nothing here reads or writes sim state, snapshots or hashes.

import type { SceneNode } from '@aod/protocol';
import { EDGE_NEIGHBOURS } from './terrainEdges.js';

export type VolcanoState = 'dormant' | 'active' | 'erupting';

/** Minimum terrain level (0..=12) of a mountain tile to host a derived volcano. */
export const VOLCANO_MIN_LEVEL = 11;
/** Percent of derived volcanoes that are `active` (stable per coordinate). */
export const VOLCANO_ACTIVE_PERCENT = 40;
/** Hard cap on volcanoes rendered (buffers are sized from this). */
export const VOLCANO_CAP = 16;

/** The tile props this module looks at (all optional: snapshots vary). */
export interface VolcanoTileProps {
  readonly q?: number;
  readonly r?: number;
  readonly biome?: string;
  readonly level?: number;
  /** Future sim-provided prop; explicit value overrides the derivation. */
  readonly volcano?: unknown;
  /** Landlocked mountain wall (sea design A5): never a volcano, never a cluster member. */
  readonly wall?: boolean;
}

export interface Volcano {
  readonly q: number;
  readonly r: number;
  readonly level: number;
  readonly state: VolcanoState;
}

function explicitState(props: VolcanoTileProps): VolcanoState | undefined {
  const v = props.volcano;
  if (v === 'dormant' || v === 'active' || v === 'erupting') return v;
  if (v === true) return 'dormant';
  return undefined;
}

/** Stable 0..99 hash of axial coordinates (no Math.random, no float noise). */
export function volcanoHash(q: number, r: number): number {
  const h = (Math.imul(q | 0, 73856093) ^ Math.imul(r | 0, 19349663)) >>> 0;
  // Extra avalanche so small coordinate steps do not correlate.
  const m = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
  return ((m ^ (m >>> 13)) >>> 0) % 100;
}

/**
 * Per-tile candidate test: an explicit `volcano` prop, or a mountain at
 * level >= VOLCANO_MIN_LEVEL. (Cluster thinning is deriveVolcanoes' job
 * because it needs neighbours.)
 */
export function isVolcano(props: VolcanoTileProps): boolean {
  if (props.wall === true) return false;
  if (explicitState(props) !== undefined) return true;
  return props.biome === 'mountain' && (props.level ?? 0) >= VOLCANO_MIN_LEVEL;
}

/** Dormant or active: explicit prop first, else stable hash of the coordinates. */
export function volcanoState(props: VolcanoTileProps): VolcanoState {
  const e = explicitState(props);
  if (e) return e;
  return volcanoHash(props.q ?? 0, props.r ?? 0) < VOLCANO_ACTIVE_PERCENT ? 'active' : 'dormant';
}

/**
 * Volcanoes on a board. A derived candidate survives only if no adjacent
 * candidate outranks it (higher level, ties broken by smaller q then r), so a
 * massif of level-11/12 peaks yields one crater at its summit. Explicit
 * `volcano` props are never thinned. Order is deterministic (by q, r).
 */
export function deriveVolcanoes(tiles: ReadonlyArray<SceneNode>, cap = VOLCANO_CAP): Volcano[] {
  const cand = new Map<string, Volcano & { readonly explicit: boolean }>();
  for (const t of tiles) {
    const p = (t.props ?? {}) as VolcanoTileProps;
    if (p.q === undefined || p.r === undefined || !isVolcano(p)) continue;
    cand.set(`${p.q},${p.r}`, {
      q: p.q,
      r: p.r,
      level: p.level ?? 0,
      state: volcanoState(p),
      explicit: explicitState(p) !== undefined,
    });
  }
  const outranks = (a: Volcano, b: Volcano) =>
    a.level !== b.level ? a.level > b.level : a.q !== b.q ? a.q < b.q : a.r < b.r;
  const out: Volcano[] = [];
  const sorted = [...cand.values()].sort((a, b) => a.q - b.q || a.r - b.r);
  for (const v of sorted) {
    if (!v.explicit) {
      const beaten = EDGE_NEIGHBOURS.some(([dq, dr]) => {
        const n = cand.get(`${v.q + dq},${v.r + dr}`);
        return n !== undefined && outranks(n, v);
      });
      if (beaten) continue;
    }
    out.push({ q: v.q, r: v.r, level: v.level, state: v.state });
    if (out.length >= cap) break;
  }
  return out;
}

/**
 * Per-instance state level handed to the shaders (instanceColor.r):
 * 0 dormant, 1 active, 2 erupting.
 */
export function volcanoLevel(state: VolcanoState): number {
  return state === 'erupting' ? 2 : state === 'active' ? 1 : 0;
}

/**
 * Per-tile shader code packed above the 6 river-edge bits of `aTone.z`
 * (value * 64): 0 = not a volcano, 1 dormant, 2 active, 3 erupting.
 */
export function volcanoTileCode(state: VolcanoState): number {
  return volcanoLevel(state) + 1;
}

/** "q,r" -> tile code for every volcano tile (empty on unlevelled boards). */
export function volcanoTileCodes(vols: ReadonlyArray<Volcano>): Map<string, number> {
  const m = new Map<string, number>();
  for (const v of vols) m.set(`${v.q},${v.r}`, volcanoTileCode(v.state));
  return m;
}

/**
 * Tiles whose scatter (rocks, snow boulders, trees, grass...) is suppressed:
 * the volcano tile itself. The cone's base (radius 0.62) plus its margin
 * stays inside the tile (apothem ~0.87), so clearing the whole tile covers
 * the footprint without touching a neighbour's scatter.
 */
export function volcanoScatterClearTiles(vols: ReadonlyArray<Volcano>): ReadonlySet<string> {
  return new Set(vols.map((v) => `${v.q},${v.r}`));
}
